import { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { IDashboard, IWidget } from '../dashboards.types'
import { CardMoveType, IAutoalignData } from '../services/autoalign/autoalign.types'
import autoalign from '../services/autoalign'
import objectUtils from '../../../utils/object.utils'
import dashboardsApi from '@/pages/Dashboards/dashboards.api.ts'
import dashboardsEvents from '@/pages/Dashboards/events/dashboards.events.ts'

interface IProps {
  viewportWidth: number
  gridStep: number
  dashboardRef: RefObject<HTMLDivElement>
  selectedDashboardId: IDashboard['id']
}

const useWidgetMoveAndResize = (props: IProps) => {
  const [isMoving, setMoving] = useState(false) // пользователь перетаскивает компонент

  // данные необходимые для рассчета координат компонентов при перетаскивании с включенным
  // автовыравниванием
  const autoalignHintRef = useRef<HTMLDivElement>()
  const autoalignDataRef = useRef<IAutoalignData>(autoalign.defaultData)
  autoalignDataRef.current.components = []
  autoalignDataRef.current.viewport = props.viewportWidth
  autoalignDataRef.current.gridStep = props.gridStep
  autoalignDataRef.current.mode = 'grid'

  // т.к. при перетаскивании и масштабировании обновления происходят с высокой частотой, то они
  // сначала сохраняются только локально и только периодически отправляются на сервер и в стейт
  const positionChanges = useRef<IWidget>(null)

  useEffect(() => {
    autoalignDataRef.current.components = []
    autoalignDataRef.current.viewport = props.viewportWidth
    autoalignDataRef.current.gridStep = props.gridStep
    autoalignDataRef.current.mode = 'grid'
    positionChanges.current = null
  }, [isMoving])

  // отмена автоматического выравнивания при нажатии на Esc
  const cancelAutoalign = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      autoalign.cancel(autoalignHintRef, autoalignDataRef)
    }
  }, [])

  // обработка перетаскивания или изменения размеров компонентов
  const handleMove = useCallback(
    (position: IWidget, type: CardMoveType) => {
      positionChanges.current = position

      if (type !== 'layer') {
        const height = parseFloat(props.dashboardRef.current.style.height)
        const edge = (position.configuration.topLeftY + position.configuration.height) * props.viewportWidth
        if (edge > height) props.dashboardRef.current.style.height = edge + 10 + 'px'

        autoalign.suggest({
          position: {
            type: position.type,
            w: position.configuration.width as any,
            h: position.configuration.height as any,
            y: position.configuration.topLeftY as any,
            x: position.configuration.topLeftX as any,
            id: position.id,
          },
          data: autoalignDataRef,
          hint: autoalignHintRef,
          oncancel: cancelAutoalign,
          height,
          type,
        })
      }

      if (!isMoving) setMoving(true)
    },
    [props.viewportWidth, isMoving]
  )

  // синхронизировать локальные изменения с сервером и стором
  const handleMoveFinish = useCallback(() => {
    const changes = objectUtils.deepCopy(positionChanges.current)

    if (changes != null) {
      // если включено автоматическое выравнивание, то берем координаты из него
      const autoposition = autoalignDataRef.current
      if (!autoposition.hidden) {
        const vw = autoposition.viewport
        changes.configuration.topLeftX = autoposition.x / vw
        changes.configuration.topLeftY = autoposition.y / vw
        changes.configuration.width = autoposition.w / vw
        changes.configuration.height = autoposition.h / vw
      }
    }

    dashboardsApi.updateWidget(changes.id, changes).then((r) => {
      if (r.status === 'success') {
        dashboardsEvents.widgetUpdated(r.body)

        autoalign.reset(autoalignHintRef, autoalignDataRef, cancelAutoalign)
        positionChanges.current = null
        setMoving(false)
      }
    })
  }, [props.selectedDashboardId])

  return { handlers: { handleMoveFinish, handleMove }, data: { isMoving }, refs: { autoalignHintRef } }
}

export default useWidgetMoveAndResize
